import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Chip, EmptyState, ErrorNotice, KV, Notice, PageHeader, Skeleton, Table } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';
import { dateOnly, money } from '@/lib/format';
import { usePublicSettings } from '@/lib/settings';
import type { BR } from '@/lib/types';
import { ratePct } from '@/features/admin/common';

interface Bank { bankName?: string; accountName?: string; iban?: string }

export function BrokerCommissions() {
  const { t, locale, localTitle } = useI18n();
  const due = useQuery({ queryKey: ['broker', 'commissions'], queryFn: () => api.get<{ items: BR[]; bank: Bank | null }>('/api/broker/commissions') });
  const done = useQuery({ queryKey: ['broker', 'requests', 'completed'], queryFn: () => api.get<{ items: BR[] }>('/api/broker/requests?status=completed&limit=200') });
  const { commissionRate } = usePublicSettings();
  const [copied, setCopied] = useState(false);
  if (due.isLoading) return <Skeleton lines={6} />;
  if (due.error) return <ErrorNotice error={due.error} retry={() => void due.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} />;
  const rate = ratePct(commissionRate);
  const bank = due.data?.bank ?? null;
  const bankSet = !!bank?.iban;
  const copy = async (text: string) => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ } };
  const history = (done.data?.items ?? []).filter((r) => r.commissionStatus !== 'none');
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/broker', label: t('nav.dashboard') }, { label: t('nav.commissions') }]} title={t('nav.commissions')} meta={t('broker.commissions.intro', { rate })} />
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
        <div className="flex flex-col gap-3">
          {due.data?.items.length ? due.data.items.map((r) => (
            <Card key={r.id} lift className="flex flex-col gap-3.5">
              <div className="flex flex-wrap justify-between gap-2">
                <div>
                  <span className="text-muted text-[13px] tabular-nums">{t('broker.commissions.saleLine', { number: r.number, title: localTitle(r), sale: money(r.commissionHalalas ? Math.round(r.commissionHalalas / (commissionRate || 0.005)) : null, locale), date: dateOnly(r.completedAt, locale) })}</span>
                  <h2 className="mt-1">{t('broker.commissions.due', { amount: money(r.commissionHalalas, locale) })}</h2>
                </div>
                <Chip form="warning">{t('broker.commissions.slotHeld')}</Chip>
              </div>
              <Card>
                <h3 className="mb-2">{t('broker.commissions.steps')}</h3>
                <ol className="ps-5 text-[14px] text-fg-2 flex flex-col gap-2 list-decimal">
                  <li>{t('broker.commissions.step1', { amount: money(r.commissionHalalas, locale) })}</li>
                  <li>{t('broker.commissions.step2', { number: r.number })}</li>
                  <li>{t('broker.commissions.step3')}</li>
                  <li>{t('broker.commissions.step4')}</li>
                </ol>
              </Card>
              {bankSet ? (
                <KV items={[
                  { k: t('broker.commissions.bank'), v: bank?.bankName || '—' },
                  { k: t('broker.commissions.accountName'), v: bank?.accountName || '—' },
                  { k: t('broker.commissions.iban'), v: <span className="ltr num">{bank?.iban} <Button variant="text" className="text-[13px] min-h-7" onClick={() => void copy(bank?.iban ?? '')}>{copied ? t('app.copied') : t('app.copy')}</Button></span> },
                ]} />
              ) : <Notice tone="warning">{t('broker.commissions.bankNotSet')}</Notice>}
            </Card>
          )) : <EmptyState title={t('broker.commissions.noDue')} />}
        </div>
        <Card>
          <h2 className="mb-3">{t('broker.commissions.history')}</h2>
          {history.length ? (
            <Table>
              <thead><tr><th>{t('broker.commissions.request')}</th><th>{t('app.commission')}</th><th>{t('app.status')}</th></tr></thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id}><td className="num">{r.number}</td><td className="num">{money(r.commissionHalalas, locale)}</td><td>{r.commissionStatus === 'due' ? <Chip form="warning">{t('broker.commissions.awaitingConfirm')}</Chip> : <Chip form="filled">{t('broker.commissions.confirmedOn', { date: dateOnly(r.commissionConfirmedAt, locale) })}</Chip>}</td></tr>
                ))}
              </tbody>
            </Table>
          ) : <p className="text-muted text-[14px]">{t('app.none')}</p>}
        </Card>
      </div>
    </div>
  );
}
